/**
 * Food orders (docs/01 §7.3).
 *
 * Participants are picked per order from **owners and/or guests** — never
 * assumed to be all three, since you are not always all in. Each person's own
 * items are theirs; delivery and service split equally by default, or
 * proportionally to what each person ordered. The discount is always
 * proportional to order value.
 *
 * Settling creates ONE expense with the computed splits, so lunch flows into
 * the same ledger as the electricity bill. A guest's line is marked
 * settled-on-the-day: it affects the arithmetic and then disappears, and never
 * reaches a balance.
 *
 * The order itself is a local draft. There is no `food_order.*` mutation yet,
 * so nothing is persisted until you settle — at which point the ledger gets the
 * expense, tagged with this draft's id so settling it twice is refused by the
 * server's `one_expense_per_source` index rather than double-counted.
 */

import { type FormEvent, useMemo, useState } from 'react'
import {
  type ExpenseId,
  type FeeSplitRule,
  type FoodOrder,
  type FoodOrderId,
  type FoodOrderParticipant,
  type MemberId,
  type TrustedClock,
  asId,
  piastres,
  uuidv7,
} from '@office/domain'
import {
  MoneyError,
  computeFoodOrderSplits,
  expenseFromFoodOrder,
  type FoodOrderSplitResult,
} from '@office/domain/money'
import type { MoneyActions } from './actions.js'
import { type MoneySnapshot, guestPredicate, nameOf } from './data.js'
import { formatInstant, parseAmount } from './format.js'
import { AmountInput, Banner, Empty, Field, Money, RadioGroup } from './ui.js'
import './money.css'

const DRAFT_EXPENSE_ID = asId<ExpenseId>('00000000-0000-7000-8000-000000000000')

interface ItemDraft {
  name: string
  price: string
}

export interface FoodOrderScreenProps {
  snapshot: MoneySnapshot
  clock: TrustedClock
  actorId: MemberId
  actions: MoneyActions
  onDone?: () => void
}

export function FoodOrderScreen({
  snapshot,
  clock,
  actorId,
  actions,
  onDone,
}: FoodOrderScreenProps) {
  // The order was started at a moment; freeze it rather than re-reading the
  // clock on every keystroke, which would also re-derive the whole split.
  const [now] = useState(() => clock.now())
  const [orderId, setOrderId] = useState<FoodOrderId>(() => asId<FoodOrderId>(uuidv7(now)))
  const [restaurant, setRestaurant] = useState('')
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set())
  const [items, setItems] = useState<Record<string, ItemDraft[]>>({})
  const [delivery, setDelivery] = useState('0')
  const [service, setService] = useState('0')
  const [discount, setDiscount] = useState('0')
  const [feeSplitRule, setFeeSplitRule] = useState<FeeSplitRule>('equal')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const isGuest = useMemo(() => guestPredicate(snapshot), [snapshot])

  const rowsOf = (id: string): ItemDraft[] => items[id] ?? [{ name: '', price: '' }]

  const badPrice = snapshot.people
    .filter(p => chosen.has(p.id))
    .some(p => rowsOf(p.id).some(r => r.price.trim() !== '' && parseAmount(r.price) === null))

  const feesValid =
    parseAmount(delivery) !== null && parseAmount(service) !== null && parseAmount(discount) !== null

  const order: FoodOrder = useMemo(() => {
    const participants: FoodOrderParticipant[] = snapshot.people
      .filter(p => chosen.has(p.id))
      .map(p => ({
        personId: p.id,
        items: (items[p.id] ?? [])
          .filter(r => r.name.trim() !== '' || r.price.trim() !== '')
          .map(r => ({ name: r.name.trim() || 'item', price: parseAmount(r.price) ?? piastres(0) })),
        notes: null,
      }))
    return {
      id: orderId,
      createdBy: actorId,
      restaurant: restaurant.trim(),
      orderedAt: now,
      status: 'open',
      participants,
      deliveryFee: parseAmount(delivery) ?? piastres(0),
      serviceFee: parseAmount(service) ?? piastres(0),
      discount: parseAmount(discount) ?? piastres(0),
      feeSplitRule,
      paidByMemberId: actorId,
      expenseId: null,
    }
  }, [snapshot.people, chosen, items, orderId, actorId, restaurant, now, delivery, service, discount, feeSplitRule])

  const computed = useMemo((): { split: FoodOrderSplitResult | null; problem: string | null } => {
    if (order.participants.length === 0) return { split: null, problem: null }
    try {
      return { split: computeFoodOrderSplits(order, { isGuest }), problem: null }
    } catch (e) {
      return {
        split: null,
        problem:
          e instanceof MoneyError && e.code === 'DISCOUNT_EXCEEDS_TOTAL'
            ? 'The discount is larger than the order.'
            : 'That order does not add up.',
      }
    }
  }, [order, isGuest])

  const canSubmit =
    restaurant.trim().length > 0 &&
    computed.split !== null &&
    computed.split.total > 0 &&
    !badPrice &&
    feesValid &&
    !busy

  function toggle(id: string) {
    setChosen(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
    setItems(cur => (cur[id] ? cur : { ...cur, [id]: [{ name: '', price: '' }] }))
  }

  function setItem(personId: string, index: number, patch: Partial<ItemDraft>) {
    setItems(cur => {
      const rows = [...(cur[personId] ?? [{ name: '', price: '' }])]
      const existing = rows[index] ?? { name: '', price: '' }
      rows[index] = { ...existing, ...patch }
      return { ...cur, [personId]: rows }
    })
  }

  function addItem(personId: string) {
    setItems(cur => ({
      ...cur,
      [personId]: [...(cur[personId] ?? [{ name: '', price: '' }]), { name: '', price: '' }],
    }))
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setBusy(true)
    setError(null)
    try {
      const expense = expenseFromFoodOrder(order, { expenseId: DRAFT_EXPENSE_ID, isGuest })
      await actions.addExpense({
        description: expense.description,
        amount: expense.amount,
        category: expense.category,
        splitKind: expense.splitKind,
        splits: expense.splits,
        source: 'food_order',
        sourceRef: order.id,
      })
      // A settled order is done with. Clear the whole draft, not just the id:
      // leaving the order on screen behind a re-enabled button is an invitation
      // to settle the same lunch twice, and the fresh id means the server's
      // `one_expense_per_source` guard would not catch it.
      setOrderId(asId<FoodOrderId>(uuidv7(clock.now())))
      setRestaurant('')
      setChosen(new Set())
      setItems({})
      setDelivery('0')
      setService('0')
      setDiscount('0')
      onDone?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not settle that order')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="m-screen" onSubmit={submit} aria-labelledby="food-title">
      <h2 className="m-title" id="food-title">Food order</h2>
      <p className="m-asof">Ordered {formatInstant(now)} · paid by {nameOf(snapshot, actorId)}</p>

      <Field id="food-restaurant" label="Restaurant">
        <input
          id="food-restaurant"
          className="m-input"
          type="text"
          value={restaurant}
          onChange={e => setRestaurant(e.target.value)}
        />
      </Field>

      <fieldset className="m-fieldset">
        <legend className="m-legend">Who is ordering?</legend>
        <div className="m-stack">
          {snapshot.people.map(p => (
            <div key={p.id} className="m-order-person">
              <label className="m-choice-label">
                <input
                  type="checkbox"
                  checked={chosen.has(p.id)}
                  onChange={() => toggle(p.id)}
                  // Described, not renamed: a guest's line settles on the day
                  // and never becomes a debt, which a screen reader user has to
                  // hear from the control itself, not from a neighbouring span.
                  aria-describedby={p.kind === 'guest' ? `guest-note-${p.id}` : undefined}
                />
                <span className="m-choice-main">{p.displayName}</span>
              </label>
              {p.kind === 'guest' && (
                <span className="m-tag" id={`guest-note-${p.id}`}>guest · settles today</span>
              )}
              {chosen.has(p.id) && (
                <div className="m-items">
                  {rowsOf(p.id).map((row, i) => (
                    <div key={i} className="m-item-row">
                      <label className="m-hint" htmlFor={`item-${p.id}-${i}-name`}>
                        {p.displayName} item {i + 1}
                      </label>
                      <input
                        id={`item-${p.id}-${i}-name`}
                        className="m-input"
                        type="text"
                        value={row.name}
                        onChange={e => setItem(p.id, i, { name: e.target.value })}
                      />
                      <label className="m-hint" htmlFor={`item-${p.id}-${i}-price`}>
                        {p.displayName} item {i + 1} price
                      </label>
                      <input
                        id={`item-${p.id}-${i}-price`}
                        className="m-input m-input-narrow"
                        type="text"
                        inputMode="decimal"
                        value={row.price}
                        aria-invalid={
                          row.price.trim() !== '' && parseAmount(row.price) === null ? true : undefined
                        }
                        onChange={e => setItem(p.id, i, { price: e.target.value })}
                      />
                    </div>
                  ))}
                  <button type="button" className="btn m-small" onClick={() => addItem(p.id)}>
                    Add an item for {p.displayName}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </fieldset>

      <AmountInput
        id="food-delivery"
        label="Delivery fee (EGP)"
        value={delivery}
        onChange={setDelivery}
        invalid={parseAmount(delivery) === null}
      />
      <AmountInput
        id="food-service"
        label="Service fee (EGP)"
        value={service}
        onChange={setService}
        invalid={parseAmount(service) === null}
      />
      <AmountInput
        id="food-discount"
        label="Discount (EGP)"
        value={discount}
        onChange={setDiscount}
        invalid={parseAmount(discount) === null}
      />

      <RadioGroup
        legend="Split the fees"
        name="food-fee-rule"
        value={feeSplitRule}
        onChange={setFeeSplitRule}
        options={[
          { value: 'equal', label: 'Equally' },
          { value: 'proportional', label: 'By order value' },
        ]}
      />

      <div className="m-preview" aria-live="polite" data-testid="food-preview">
        {computed.problem !== null ? (
          <Banner tone="warn">{computed.problem}</Banner>
        ) : computed.split === null ? (
          <Empty mark="🍴" hint="Participants are chosen per order, from owners and guests alike — you are not always all in.">
            Pick who is ordering to see the split.
          </Empty>
        ) : (
          <table className="m-table">
            <caption className="m-caption">What each person owes</caption>
            <thead>
              <tr>
                <th scope="col">Person</th>
                <th scope="col">Items</th>
                <th scope="col">Fees</th>
                <th scope="col">Discount</th>
                <th scope="col">Total</th>
              </tr>
            </thead>
            <tbody>
              {computed.split.lines.map(line => (
                <tr key={line.personId}>
                  <th scope="row">
                    {nameOf(snapshot, line.personId)}
                    {line.settledImmediately && <span className="m-tag"> settles today</span>}
                  </th>
                  <td><Money value={line.itemsTotal} /></td>
                  <td><Money value={line.feeShare} /></td>
                  <td><Money value={line.discountShare} /></td>
                  <td><Money value={line.total} /></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row" colSpan={4}>Order total</th>
                <td data-testid="food-total"><Money value={computed.split.total} /></td>
              </tr>
            </tfoot>
          </table>
        )}
        {badPrice && <Banner tone="warn">One of the prices is not a number.</Banner>}
        {!feesValid && (
          <Banner tone="warn">
            The delivery fee, the service fee and the discount each need an amount. Use 0 for
            none.
          </Banner>
        )}
      </div>

      {error !== null && <Banner tone="danger">{error}</Banner>}

      <div className="m-actions">
        <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
          Settle into the ledger
        </button>
      </div>
    </form>
  )
}
